import React, { useState, useEffect, useCallback } from 'react';
import apiClient from './services/api';
import './ReservationCalendar.css';

interface CalendarDay {
  date: string;
  availableCount: number;
  totalRooms: number;
  status: 'full' | 'empty' | 'partial';
}

interface ReservationCalendarProps {
  onDateSelect: (checkIn: string, checkOut: string) => void;
  initialCheckIn?: string;
  initialCheckOut?: string;
}

const ReservationCalendar: React.FC<ReservationCalendarProps> = ({ 
  onDateSelect, 
  initialCheckIn,
  initialCheckOut 
}) => {
  const [calendarData, setCalendarData] = useState<{ [key: string]: CalendarDay }>({});
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [checkInDate, setCheckInDate] = useState<string>(initialCheckIn || '');
  const [checkOutDate, setCheckOutDate] = useState<string>(initialCheckOut || '');
  const [selectingCheckOut, setSelectingCheckOut] = useState(false);
  const [hoveredDate, setHoveredDate] = useState<string | null>(null);

  const fetchCalendarData = useCallback(async (year: number, month: number) => {
    try {
      setLoading(true);
      const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;
      const response = await apiClient.get('/rooms/calendar', { params: { month: monthStr } });
      
      if (response.data.success) {
        setCalendarData(prev => ({
          ...prev,
          ...response.data.data.calendar
        }));
      }
    } catch (error) {
      console.error('Calendar fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Mevcut ay ve sonraki ayı çek
    fetchCalendarData(currentMonth.year, currentMonth.month);
    
    // Sonraki ayı da önceden yükle
    const nextMonth = currentMonth.month === 11 
      ? { year: currentMonth.year + 1, month: 0 }
      : { year: currentMonth.year, month: currentMonth.month + 1 };
    fetchCalendarData(nextMonth.year, nextMonth.month);
  }, [currentMonth, fetchCalendarData]);

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      if (direction === 'next') {
        return prev.month === 11 
          ? { year: prev.year + 1, month: 0 }
          : { year: prev.year, month: prev.month + 1 };
      } else {
        return prev.month === 0 
          ? { year: prev.year - 1, month: 11 }
          : { year: prev.year, month: prev.month - 1 };
      }
    });
  };

  const getFirstFullDateAfter = useCallback((afterDate: string): string | null => {
    const sortedDates = Object.keys(calendarData).sort();

    for (const dateStr of sortedDates) {
      if (dateStr <= afterDate) continue;

      const dayData = calendarData[dateStr];
      if (dayData?.status === 'full') {
        return dateStr;
      }
    }

    return null;
  }, [calendarData]);

  const isDateDisabled = (dateStr: string): boolean => {
    const today = new Date().toISOString().split('T')[0];
    if (dateStr < today) return true;

    const dayData = calendarData[dateStr];

    // Check-out seçiminde ilk dolu gün boundary olarak seçilebilir.
    if (selectingCheckOut && checkInDate && dateStr > checkInDate) {
      const boundary = getFirstFullDateAfter(checkInDate);
      if (boundary) {
        return dateStr > boundary;
      }
    }

    // Eğer tüm odalar doluysa disabled
    if (dayData && dayData.status === 'full') {
      if (selectingCheckOut && checkInDate && dateStr === getFirstFullDateAfter(checkInDate)) {
        return false;
      }
      return true;
    }
    
    return false;
  };

  const isDateInRange = (dateStr: string): boolean => {
    if (!checkInDate || !checkOutDate) return false;
    return dateStr > checkInDate && dateStr < checkOutDate;
  };

  const isHoveredInRange = (dateStr: string): boolean => {
    if (!selectingCheckOut || !checkInDate || !hoveredDate) return false;
    return dateStr > checkInDate && dateStr <= hoveredDate;
  };

  const isCheckoutBoundary = (dateStr: string): boolean => {
    if (!selectingCheckOut || !checkInDate || checkOutDate) return false;
    return getFirstFullDateAfter(checkInDate) === dateStr;
  };

  const handleDateClick = (dateStr: string) => {
    if (isDateDisabled(dateStr)) return;

    if (!selectingCheckOut) {
      // Check-in seçimi
      setCheckInDate(dateStr);
      setCheckOutDate('');
      setSelectingCheckOut(true);
    } else {
      // Check-out seçimi
      if (dateStr <= checkInDate) {
        // Eğer seçilen tarih check-in'den önceyse, yeni check-in olarak ayarla
        setCheckInDate(dateStr);
        setCheckOutDate('');
      } else {
        setCheckOutDate(dateStr);
        setSelectingCheckOut(false);
        onDateSelect(checkInDate, dateStr);
      }
    }
  };

  const resetSelection = () => {
    setCheckInDate('');
    setCheckOutDate('');
    setSelectingCheckOut(false);
  };

  const calculateNights = (): number => {
    if (!checkInDate || !checkOutDate) return 0;
    const start = new Date(checkInDate);
    const end = new Date(checkOutDate);
    return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const renderCalendarDays = () => {
    const { year, month } = currentMonth;
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDay = (firstDay.getDay() + 6) % 7; // Pazartesi = 0
    const daysInMonth = lastDay.getDate();

    const days: React.ReactNode[] = [];

    // Önceki aydan boş günler
    for (let i = 0; i < startingDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day placeholder"></div>);
    }

    // Ayın günleri
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayData = calendarData[dateStr];
      const isDisabled = isDateDisabled(dateStr);
      const isCheckIn = dateStr === checkInDate;
      const isCheckOut = dateStr === checkOutDate;
      const isInRange = isDateInRange(dateStr);
      const isHovered = isHoveredInRange(dateStr);
      const isCheckoutBoundaryDay = isCheckoutBoundary(dateStr);
      const isToday = dateStr === new Date().toISOString().split('T')[0];

      let statusClass = '';
      if (dayData) {
        if (dayData.status === 'full') statusClass = 'full';
        else if (dayData.status === 'empty') statusClass = 'available';
        else statusClass = 'partial';
      }

      const classNames = [
        'calendar-day',
        statusClass,
        isDisabled ? 'disabled' : '',
        isCheckIn ? 'check-in' : '',
        isCheckOut ? 'check-out' : '',
        isInRange ? 'in-range' : '',
        isHovered ? 'hover-range' : '',
        isCheckoutBoundaryDay ? 'check-out' : '',
        isToday ? 'today' : ''
      ].filter(Boolean).join(' ');

      days.push(
        <div
          key={dateStr}
          className={classNames}
          onClick={() => handleDateClick(dateStr)}
          onMouseEnter={() => setHoveredDate(dateStr)}
          onMouseLeave={() => setHoveredDate(null)}
          title={dayData ? `${dayData.availableCount}/${dayData.totalRooms} oda müsait` : ''}
        >
          <span className="day-number">{day}</span>
          {dayData && !isDisabled && (
            <span className="availability-dot">
              {dayData.availableCount > 0 ? dayData.availableCount : ''}
            </span>
          )}
        </div>
      );
    }

    return days;
  };

  const monthName = new Date(currentMonth.year, currentMonth.month).toLocaleDateString('tr-TR', { 
    month: 'long', 
    year: 'numeric' 
  });

  // Geçmiş aya gitmeyi engelle
  const canGoPrev = () => {
    const now = new Date();
    return currentMonth.year > now.getFullYear() || 
      (currentMonth.year === now.getFullYear() && currentMonth.month > now.getMonth());
  };

  return (
    <div className="reservation-calendar">
      <div className="calendar-instruction">
        {!selectingCheckOut ? (
          <p>📅 <strong>Giriş tarihinizi</strong> seçin</p>
        ) : (
          <p>📅 <strong>Çıkış tarihinizi</strong> seçin</p>
        )}
      </div>

      <div className="calendar-header">
        <button 
          className="nav-btn" 
          onClick={() => navigateMonth('prev')}
          disabled={!canGoPrev()}
        >
          ‹
        </button>
        <h3>{monthName}</h3>
        <button className="nav-btn" onClick={() => navigateMonth('next')}>
          ›
        </button>
      </div>

      <div className="calendar-legend">
        <span className="legend-item available">
          <span className="legend-dot"></span> Müsait
        </span>
        <span className="legend-item partial">
          <span className="legend-dot"></span> Kısmi
        </span>
        <span className="legend-item full">
          <span className="legend-dot"></span> Dolu
        </span>
      </div>

      <div className="calendar-weekdays">
        <span>Pzt</span>
        <span>Sal</span>
        <span>Çar</span>
        <span>Per</span>
        <span>Cum</span>
        <span>Cmt</span>
        <span>Paz</span>
      </div>

      <div className="calendar-grid">
        {loading && Object.keys(calendarData).length === 0 ? (
          <div className="loading-overlay">Yükleniyor...</div>
        ) : (
          renderCalendarDays()
        )}
      </div>

      {/* Seçim Özeti */}
      {(checkInDate || checkOutDate) && (
        <div className="selection-summary">
          <div className="selected-dates">
            <div className="date-box">
              <span className="date-label">Giriş</span>
              <span className="date-value">
                {checkInDate ? formatDate(checkInDate) : '---'}
              </span>
            </div>
            <div className="date-arrow">→</div>
            <div className="date-box">
              <span className="date-label">Çıkış</span>
              <span className="date-value">
                {checkOutDate ? formatDate(checkOutDate) : '---'}
              </span>
            </div>
          </div>
          
          {checkInDate && checkOutDate && (
            <div className="nights-info">
              <span className="nights-count">{calculateNights()} Gece</span>
            </div>
          )}

          <button className="reset-btn" onClick={resetSelection}>
            Seçimi Sıfırla
          </button>
        </div>
      )}
    </div>
  );
};

export default ReservationCalendar;
